import { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { MessageSquare, X, Camera, Trash2, Loader2, Mic, ArrowRightCircle, RotateCcw, Check, CalendarDays, CloudOff } from 'lucide-react';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { compressImageSafeCanvas } from '@/lib/image-compression';
import { StoragePhoto } from '@/components/StoragePhoto';
import { isLocalPhotoRef } from '@/lib/storageUrls';
import { format } from 'date-fns';
import { pl } from 'date-fns/locale/pl';

/** Popover do wyboru daty przełożenia zadania SOP */
function ReschedulePopover({
  taskId,
  disabled,
  isSaving,
  onReschedule,
}: {
  taskId: string;
  disabled: boolean;
  isSaving: boolean;
  onReschedule: (id: string, targetDate: Date) => void;
}) {
  const [open, setOpen] = useState(false);

  const handleReschedule = (targetDate: Date) => {
    if (disabled || isSaving) return;
    const d = new Date(targetDate);
    d.setHours(0, 0, 0, 0);
    onReschedule(taskId, d);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          disabled={disabled || isSaving}
          className="h-10 w-10 text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
          title="Przełóż zadanie"
        >
          <ArrowRightCircle className="h-6 w-6" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="end">
        <div className="p-3 space-y-3">
          <p className="text-sm font-medium text-muted-foreground">Przełóż na datę</p>
          <Button
            variant="outline"
            size="sm"
            className="w-full justify-start"
            onClick={() => {
              const tomorrow = new Date();
              tomorrow.setDate(tomorrow.getDate() + 1);
              tomorrow.setHours(0, 0, 0, 0);
              handleReschedule(tomorrow);
            }}
          >
            <CalendarDays className="mr-2 h-4 w-4" />
            Przenieś na jutro
          </Button>
          <Calendar
            mode="single"
            disabled={(date) => {
              const today = new Date();
              today.setHours(0, 0, 0, 0);
              const d = new Date(date);
              d.setHours(0, 0, 0, 0);
              return d.getTime() <= today.getTime(); // Blokuj dziś i w przeszłości
            }}
            onSelect={(date) => date && handleReschedule(date)}
            initialFocus
          />
        </div>
      </PopoverContent>
    </Popover>
  );
}

/** disabled: powinien zależeć tylko od !isWorkStarted – gdy praca trwa, notatki i SOP są aktywne. */
interface ChecklistItemProps {
  task: {
    id: string;
    name: string;
    completed: boolean;
    note?: string;
    task_type?: 'sop_standard' | 'coordinator_single' | 'long_term' | 'employee_extra'; // Task type from cleaning_tasks.task_type
    status?: 'active' | 'moved'; // Status zadania - 'moved' oznacza przeniesione na inną datę
    moved_to_task_id?: string; // ID kopii na docelową datę (dla cofnięcia przeniesienia)
    moved_to_date?: string; // ISO string daty docelowej (np. 2026-03-10T00:00:00.000Z)
    requiresPhoto?: boolean; // Legacy field
    photoTaken?: boolean; // Legacy field
    photo_required?: boolean; // Wymagane zdjęcie (z JSON checklisty)
    min_photos?: number; // Minimalna liczba zdjęć (z JSON checklisty)
    photo_urls?: string[]; // Tablica URL-i zdjęć (z JSON checklisty)
    is_extra_task?: boolean; // Czy zadanie jest dodatkowe (dodane przez użytkownika)
    coordinator_notes?: string | null; // Notatki koordynatora (dla coordinator_single i employee_extra)
  };
  disabled?: boolean;
  isSaving?: boolean; // Blokada synchronizacji podczas zapisu
  onToggle: (id: string) => void;
  onNoteChange: (id: string, note: string) => void;
  onPhotoTaken?: (id: string) => void; // Legacy callback
  onPhotoUpload?: (id: string, files: File[], index?: number) => void; // Nowy callback dla uploadu zdjęć (index: 0=Przed, 1=Po)
  onPhotoDelete?: (id: string, photoUrl: string) => void; // Callback dla usuwania zdjęć
  onDelete?: (id: string) => void; // Callback dla usuwania zadań dodatkowych
  onRescheduleTask?: (id: string, targetDate: Date) => void; // Callback dla przełożenia zadania na wybraną datę (tylko dla sop_standard)
  onUndoMove?: (id: string) => void; // Callback dla cofnięcia przeniesienia zadania (tylko dla zadań z statusem 'moved')
  onUndoComplete?: (id: string) => void; // Callback dla cofnięcia ukończenia zadania (tylko dla zadań z completed === true)
}

export const ChecklistItem = ({ 
  task, 
  disabled = false,
  isSaving = false, // Domyślnie false
  onToggle, 
  onNoteChange, 
  onPhotoTaken,
  onPhotoUpload,
  onPhotoDelete,
  onDelete,
  onRescheduleTask,
  onUndoMove,
  onUndoComplete
}: ChecklistItemProps) => {
  const [showNoteInput, setShowNoteInput] = useState(false);
  const [localNote, setLocalNote] = useState(task.note || ''); // Lokalny "brudnopis" - tylko do edycji
  const [isUploading, setIsUploading] = useState(false);
  const [isProcessingImage, setIsProcessingImage] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [uploadingIndex, setUploadingIndex] = useState<number | null>(null); // Index aktualnie uploadowanego zdjęcia (0=Przed, 1=Po)
  
  // Dwuetapowy mechanizm zdjęć: Podgląd + Akceptacja
  // Dla standardowych zadań (nie is_extra_task)
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  
  // Dla zadań dodatkowych (is_extra_task) - osobne stany dla Przed/Po
  const [selectedFileBefore, setSelectedFileBefore] = useState<File | null>(null);
  const [previewUrlBefore, setPreviewUrlBefore] = useState<string | null>(null);
  const [selectedFileAfter, setSelectedFileAfter] = useState<File | null>(null);
  const [previewUrlAfter, setPreviewUrlAfter] = useState<string | null>(null);
  
  // Stan dla informacji o przywróconych draftach (dla wizualizacji)
  const [restoredDraft, setRestoredDraft] = useState<{ note?: boolean; photo?: boolean } | null>(null);
  
  // Refs dla standardowych zadań: cameraInputRef (z capture="environment")
  const cameraInputRef = useRef<HTMLInputElement>(null);
  
  // Refs dla zadań dodatkowych (zachowane dla kompatybilności)
  const fileInputRefBefore = useRef<HTMLInputElement>(null); // Input dla "Foto Przed"
  const fileInputRefAfter = useRef<HTMLInputElement>(null); // Input dla "Foto Po"
  
  const recognitionRef = useRef<SpeechRecognition | null>(null);
  const baseTextRef = useRef<string>('');
  
  // Klucze localStorage dla draftów
  const draftNoteKey = `draft_note_${task.id}`;

  // Obsługuj zarówno nowe parametry (photo_required, min_photos) jak i legacy (requiresPhoto)
  const photo_required = task.photo_required ?? task.requiresPhoto ?? false;
  const min_photos = task.min_photos ?? 1;
  const photo_urls = task.photo_urls ?? [];
  const hasEnoughPhotos = photo_urls.length >= min_photos;

  const hasPhoto = Boolean(previewUrl || (photo_urls && photo_urls.length > 0));
  const hasPhotoBefore = Boolean(previewUrlBefore || (photo_urls && photo_urls[0]));
  const hasPhotoAfter = Boolean(previewUrlAfter || (photo_urls && photo_urls[1]));

  // Sprawdź czy checkbox powinien być zablokowany
  // FIX: Zablokuj checkbox dla zadań z statusem 'moved' - nie można ich odhaczyć
  const isCheckboxDisabled = disabled || task.status === 'moved' || (photo_required && !hasEnoughPhotos && !task.completed);

  // Funkcja do obsługi zmiany notatki w lokalnym "brudnopisie" - NIE wywołuje onNoteChange
  const handleNoteChange = (value: string) => {
    setLocalNote(value);
    // Auto-save: Zapisz do localStorage przy każdej zmianie
    if (typeof window !== 'undefined') {
      try {
        if (value.trim()) {
          localStorage.setItem(draftNoteKey, value);
        } else {
          localStorage.removeItem(draftNoteKey);
        }
      } catch (error) {
        console.error('[ChecklistItem] Błąd zapisu draftu notatki do localStorage:', error);
      }
    }
    // NIE wywołuj onNoteChange - zapis tylko po kliknięciu "Zapisz"
  };

  // Przywróć drafty z localStorage przy mount komponentu
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const timers: ReturnType<typeof setTimeout>[] = [];

    try {
      // Przywróć draft notatki
      const savedNote = localStorage.getItem(draftNoteKey);
      if (savedNote && savedNote.trim()) {
        setLocalNote(savedNote);
        baseTextRef.current = savedNote;
        setRestoredDraft(prev => ({ ...prev, note: true }));
        // Ukryj komunikat po 3 sekundach
        timers.push(
          setTimeout(() => {
            setRestoredDraft(prev => prev ? { ...prev, note: false } : null);
          }, 3000)
        );
      }
      
    } catch (error) {
      console.error('[ChecklistItem] Błąd przywracania draftów z localStorage:', error);
    }

    return () => {
      timers.forEach(clearTimeout);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [task.id]); // Tylko przy mount lub zmianie task.id

  // Inicjalizuj localNote gdy otwieramy edycję (tylko przy otwarciu, nie przy zmianie task.note)
  useEffect(() => {
    if (showNoteInput) {
      // Sprawdź czy jest draft w localStorage - jeśli tak, użyj go, jeśli nie, użyj task.note
      if (typeof window !== 'undefined') {
        try {
          const savedNote = localStorage.getItem(draftNoteKey);
          if (savedNote && savedNote.trim()) {
            setLocalNote(savedNote);
            baseTextRef.current = savedNote;
          } else {
            setLocalNote(task.note || '');
            baseTextRef.current = task.note || '';
          }
        } catch (error) {
          console.error('[ChecklistItem] Błąd odczytu draftu notatki:', error);
          setLocalNote(task.note || '');
          baseTextRef.current = task.note || '';
        }
      } else {
        setLocalNote(task.note || '');
        baseTextRef.current = task.note || '';
      }
    }
  }, [showNoteInput, draftNoteKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Synchronizuj localNote z task.note gdy się zmienia z zewnątrz (np. po odświeżeniu)
  // Tylko gdy input nie jest otwarty i zapis nie jest w toku, aby nie nadpisywać lokalnych zmian
  // OCHRONA: Nie nadpisuj localNote podczas zapisu lub gdy jest edytowane
  useEffect(() => {
    // Porównaj znormalizowane wartości (trimmed) aby uniknąć fałszywych różnic
    const taskNoteTrimmed = (task.note || '').trim();
    const localNoteTrimmed = (localNote || '').trim();
    
    // Aktualizuj tylko jeśli wartości faktycznie się różnią i nie ma aktywnych edycji
    // WAŻNE: Nie synchronizuj podczas zapisu (isSaving), aby uniknąć nadpisywania zmian w trakcie
    // WAŻNE: Nie synchronizuj gdy input jest otwarty, aby nie przerywać edycji użytkownika
    if (!showNoteInput && !isSaving && taskNoteTrimmed !== localNoteTrimmed) {
      setLocalNote(task.note || '');
      console.log('[ChecklistItem] Zsynchronizowano notatkę z zewnątrz:', task.id, 'stara:', localNoteTrimmed, 'nowa:', taskNoteTrimmed);
    }
  }, [task.note, showNoteInput, isSaving]); // eslint-disable-line react-hooks/exhaustive-deps

  // Initialize Speech Recognition
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const SpeechRecognition = window.SpeechRecognition ?? window.webkitSpeechRecognition;
      
      if (SpeechRecognition) {
        const recognition = new SpeechRecognition();
        recognition.continuous = true;
        recognition.interimResults = true;
        recognition.lang = 'pl-PL';

        recognition.onresult = (event: SpeechRecognitionEvent) => {
          let interimTranscript = '';
          let finalTranscript = '';

          for (let i = event.resultIndex; i < event.results.length; i++) {
            const transcript = event.results[i][0].transcript;
            if (event.results[i].isFinal) {
              finalTranscript += transcript + ' ';
            } else {
              interimTranscript += transcript;
            }
          }

          // Use base text from start of recording + new transcript to avoid duplicates
          const baseText = baseTextRef.current.trim();
          const fullTranscript = finalTranscript + interimTranscript;
          const newText = baseText ? `${baseText} ${fullTranscript}`.trim() : fullTranscript.trim();
          
          // Ustaw nową wartość w lokalnym "brudnopisie" - NIE wywołuj onNoteChange
          setLocalNote(newText);
        };

        recognition.onerror = (event: any) => {
          console.error('[ChecklistItem] Speech recognition error:', event.error);
          setIsListening(false);
          
          if (event.error === 'no-speech') {
            toast.info('Nie wykryto mowy. Spróbuj ponownie.');
          } else if (event.error === 'audio-capture') {
            toast.error('Nie można uzyskać dostępu do mikrofonu.');
          } else if (event.error === 'not-allowed') {
            toast.error('Brak uprawnień do mikrofonu.');
          }
        };

        recognition.onend = () => {
          setIsListening(false);
        };

        recognitionRef.current = recognition;
      }
    }

    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
    };
  }, [task.id, onNoteChange]);

  // Handler dla standardowych zadań (dwuetapowy mechanizm)
  const handleCameraClick = () => {
    if (disabled || isUploading) return;
    cameraInputRef.current?.click();
  };

  // Handlery dla zadań dodatkowych (zachowane dla kompatybilności)
  const handlePhotoClickBefore = () => {
    if (disabled || isUploading) return;
    fileInputRefBefore.current?.click();
  };

  const handlePhotoClickAfter = () => {
    if (disabled || isUploading) return;
    fileInputRefAfter.current?.click();
  };

  // Dwuetapowy mechanizm: handleFileChange TYLKO generuje podgląd (nie wywołuje onPhotoUpload)
  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>, index?: number) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    // Waliduj typy plików
    const imageFiles = Array.from(files).filter(file => {
      const isValidType = file.type.startsWith('image/');
      if (!isValidType) {
        toast.error(`${file.name} nie jest obrazem`);
      }
      return isValidType;
    });

    if (imageFiles.length === 0) {
      toast.error('Wybierz przynajmniej jeden plik graficzny');
      return;
    }

    // NATYCHMIASTOWA kompresja – "zamrażamy" ulotny plik z aparatu w trwały obiekt
    const rawFile = imageFiles[0];
    let compressedFile: File;
    setIsProcessingImage(true);
    // Wymuszenie wyrenderowania loadera przed cięższą operacją.
    await new Promise(resolve => setTimeout(resolve, 100));
    try {
      compressedFile = await compressImageSafeCanvas(rawFile, {
        maxWidthOrHeight: 1024,
        initialQuality: 0.6,
      });
      console.log('Plik natychmiast skompresowany i zabezpieczony:', compressedFile.size);
    } catch (error) {
      console.error('[ChecklistItem] Błąd kompresji przy wyborze pliku:', error);
      toast.error('Nie udało się przetworzyć zdjęcia');
      return;
    } finally {
      setIsProcessingImage(false);
    }

    // Dla zadań dodatkowych (is_extra_task) - dwuetapowy mechanizm z podglądem
    if (task.is_extra_task && typeof index === 'number') {
      if (index === 0) {
        setSelectedFileBefore(compressedFile);
        const objectUrl = URL.createObjectURL(compressedFile);
        setPreviewUrlBefore(objectUrl);
      } else if (index === 1) {
        setSelectedFileAfter(compressedFile);
        const objectUrl = URL.createObjectURL(compressedFile);
        setPreviewUrlAfter(objectUrl);
      }
      if (index === 0 && fileInputRefBefore.current) {
        fileInputRefBefore.current.value = '';
      } else if (index === 1 && fileInputRefAfter.current) {
        fileInputRefAfter.current.value = '';
      }
      return;
    }

    // Dla standardowych zadań
    setSelectedFile(compressedFile);
    const objectUrl = URL.createObjectURL(compressedFile);
    setPreviewUrl(objectUrl);
    if (cameraInputRef.current) {
      cameraInputRef.current.value = '';
    }
  };

  // Funkcje do czyszczenia podglądu (z czyszczeniem localStorage)
  const handleClearPreview = () => {
    if (previewUrl) {
      // Jeśli to URL.createObjectURL, zwolnij go
      if (previewUrl.startsWith('blob:')) {
        URL.revokeObjectURL(previewUrl);
      }
    }
    setSelectedFile(null);
    setPreviewUrl(null);
    
  };

  const handleClearPreviewBefore = () => {
    if (previewUrlBefore) {
      // Jeśli to URL.createObjectURL, zwolnij go
      if (previewUrlBefore.startsWith('blob:')) {
        URL.revokeObjectURL(previewUrlBefore);
      }
    }
    setSelectedFileBefore(null);
    setPreviewUrlBefore(null);
    
  };

  const handleClearPreviewAfter = () => {
    if (previewUrlAfter) {
      // Jeśli to URL.createObjectURL, zwolnij go
      if (previewUrlAfter.startsWith('blob:')) {
        URL.revokeObjectURL(previewUrlAfter);
      }
    }
    setSelectedFileAfter(null);
    setPreviewUrlAfter(null);
    
  };

  // Funkcja do wysyłki zdjęcia standardowego (przycisk Zapisz)
  // Plik już skompresowany w handleFileChange – używamy selectedFile ze stanu.
  const handlePhotoSend = async () => {
    if (!selectedFile || !onPhotoUpload || disabled || isUploading) return;

    setIsUploading(true);
    try {
      await onPhotoUpload(task.id, [selectedFile]);
      handleClearPreview();
    } catch (error) {
      console.error('[ChecklistItem] Błąd podczas wysyłki zdjęcia:', error);
      const errorMessage = error instanceof Error ? error.message : 'Nieznany błąd';
      toast.error(`Błąd podczas wysyłki zdjęcia: ${errorMessage}`);
    } finally {
      setIsUploading(false);
    }
  };

  // Funkcja do wysyłki zdjęcia "Przed" (dla zadań dodatkowych)
  // Plik już skompresowany w handleFileChange – używamy selectedFileBefore ze stanu.
  const handlePhotoSendBefore = async () => {
    if (!selectedFileBefore || !onPhotoUpload || disabled || isUploading) return;

    setUploadingIndex(0);
    setIsUploading(true);
    try {
      await onPhotoUpload(task.id, [selectedFileBefore], 0);
      handleClearPreviewBefore();
    } catch (error) {
      console.error('[ChecklistItem] Błąd podczas wysyłki zdjęcia "Przed":', error);
      const errorMessage = error instanceof Error ? error.message : 'Nieznany błąd';
      toast.error(`Błąd podczas wysyłki zdjęcia: ${errorMessage}`);
    } finally {
      setIsUploading(false);
      setUploadingIndex(null);
    }
  };

  // Funkcja do wysyłki zdjęcia "Po" (dla zadań dodatkowych)
  // Plik już skompresowany w handleFileChange – używamy selectedFileAfter ze stanu.
  const handlePhotoSendAfter = async () => {
    if (!selectedFileAfter || !onPhotoUpload || disabled || isUploading) return;

    setUploadingIndex(1);
    setIsUploading(true);
    try {
      await onPhotoUpload(task.id, [selectedFileAfter], 1);
      handleClearPreviewAfter();
    } catch (error) {
      console.error('[ChecklistItem] Błąd podczas wysyłki zdjęcia "Po":', error);
      const errorMessage = error instanceof Error ? error.message : 'Nieznany błąd';
      toast.error(`Błąd podczas wysyłki zdjęcia: ${errorMessage}`);
    } finally {
      setIsUploading(false);
      setUploadingIndex(null);
    }
  };

  // Cleanup: zwolnij URL.createObjectURL przy unmount
  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
      if (previewUrlBefore) {
        URL.revokeObjectURL(previewUrlBefore);
      }
      if (previewUrlAfter) {
        URL.revokeObjectURL(previewUrlAfter);
      }
    };
  }, [previewUrl, previewUrlBefore, previewUrlAfter]);

  const handlePhotoDelete = async (photoUrl: string) => {
    if (disabled || isUploading) return;

    if (!onPhotoDelete) {
      toast.error('Funkcja usuwania zdjęć nie jest dostępna');
      return;
    }

    const confirmed = window.confirm('Czy na pewno chcesz usunąć to zdjęcie?');
    if (!confirmed) return;

    const isLocalUrl = photoUrl.startsWith('data:') || photoUrl.startsWith('blob:');

    setIsUploading(true);
    try {
      await onPhotoDelete(task.id, photoUrl);
    } catch (error) {
      console.error('[ChecklistItem] Błąd usuwania:', error);
    } finally {
      setIsUploading(false);
    }
  };

  const handleNoteSubmit = () => {
    // Zapisz notatkę do bazy danych przez wywołanie callbacku
    onNoteChange(task.id, localNote);
    
    // Wyczyść draft z localStorage po zapisie
    if (typeof window !== 'undefined') {
      try {
        localStorage.removeItem(draftNoteKey);
      } catch (error) {
        console.error('[ChecklistItem] Błąd usuwania draftu notatki z localStorage:', error);
      }
    }
    
    // Zamknij pole edycji
    setShowNoteInput(false);
  };

  const handleCancel = () => {
    // Resetuj localNote do oryginalnej wartości z task.note
    setLocalNote(task.note || '');
    
    // Wyczyść draft z localStorage po anulowaniu
    if (typeof window !== 'undefined') {
      try {
        localStorage.removeItem(draftNoteKey);
      } catch (error) {
        console.error('[ChecklistItem] Błąd usuwania draftu notatki z localStorage:', error);
      }
    }
    
    // Zamknij pole edycji
    setShowNoteInput(false);
  };

  const handleVoiceInput = () => {
    if (disabled || !recognitionRef.current) {
      toast.error('Rozpoznawanie mowy nie jest dostępne');
      return;
    }

    const recognition = recognitionRef.current;

    if (isListening) {
      // Stop recording
      try {
        recognition.stop();
        setIsListening(false);
        baseTextRef.current = localNote; // Zapisz aktualną wartość jako bazę
      } catch (error) {
        console.error('[ChecklistItem] Błąd zatrzymania rozpoznawania:', error);
      }
    } else {
      // Start recording
      baseTextRef.current = localNote; // Zapisz aktualną wartość jako bazę
      try {
        recognition.start();
        setIsListening(true);
        toast.info('Rozpoznawanie mowy rozpoczęte...');
      } catch (error) {
        console.error('[ChecklistItem] Błąd uruchomienia rozpoznawania:', error);
        toast.error('Nie udało się uruchomić rozpoznawania mowy');
        setIsListening(false);
      }
    }
  };

  return (
    <div className="space-y-2">
      <div className={cn(
        "flex items-center gap-3 p-3 rounded-lg bg-white dark:bg-zinc-900 shadow-md border border-black/5 dark:border-white/10",
        disabled && "opacity-60",
        isCheckboxDisabled && !task.completed && "opacity-75",
        task.status === 'moved' && "opacity-50" // FIX: Wyszarz zadania przeniesione
      )}>
        {/* Large toggle switch */}
        <button
          onClick={() => !isCheckboxDisabled && onToggle(task.id)}
          disabled={isCheckboxDisabled}
          className={cn(
            "relative h-10 w-16 rounded-full transition-all duration-300 flex-shrink-0",
            "focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary",
            task.completed 
              ? "bg-green-500 dark:bg-green-600" 
              : "bg-red-400 dark:bg-red-500",
            isCheckboxDisabled && "cursor-not-allowed opacity-50"
          )}
          title={
            isCheckboxDisabled && photo_required && !hasEnoughPhotos
              ? `Wymagane minimum ${min_photos} ${min_photos === 1 ? 'zdjęcie' : 'zdjęć'}. Obecnie: ${photo_urls.length}`
              : undefined
          }
        >
          <span
            className={cn(
              "absolute top-1 h-8 w-8 rounded-full bg-white shadow-md transition-all duration-300",
              task.completed ? "left-7" : "left-1"
            )}
          />
        </button>

        {/* Task name */}
        <div className="flex-1">
          <span className={cn(
            "text-lg font-semibold transition-all block",
            task.completed && "line-through text-muted-foreground"
          )}>
            {task.name}
          </span>
          
          {/* Ikony statusu poniżej nazwy zadania */}
          <div className="flex flex-row flex-wrap gap-2 mt-1.5 items-center">
            {/* Badge typu zadania */}
            {task.is_extra_task && (
              <Badge variant="secondary" className="text-xs bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300">
                Dodatkowe
              </Badge>
            )}
            
            {/* Badge przeniesienia */}
            {task.status === 'moved' && (
              <>
                <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 dark:bg-blue-950 dark:text-blue-300 border-blue-300 dark:border-blue-700">
                  Przeniesione
                </Badge>
                <span className="text-xs text-muted-foreground italic">
                  {task.moved_to_date
                    ? `Przeniesione na: ${format(new Date(task.moved_to_date), 'dd.MM.yyyy', { locale: pl })}`
                    : 'Przeniesione'}
                </span>
              </>
            )}
            
            {/* Licznik zdjęć */}
            {photo_required && (
              <span className="text-xs text-muted-foreground">
                ({photo_urls.length}/{min_photos})
              </span>
            )}
            
            {/* Przycisk notatki */}
            <Button
              variant="ghost"
              size="icon"
              disabled={disabled}
              className={cn(
                "h-10 w-10",
                task.note && task.note.trim() !== '' && "text-primary"
              )}
              onClick={() => !disabled && setShowNoteInput(!showNoteInput)}
              title={task.note && task.note.trim() !== '' ? 'Edytuj notatkę' : 'Dodaj notatkę'}
            >
              <MessageSquare className="h-6 w-6" />
            </Button>
            
            {/* Przyciski zdjęć - standardowe zadania (m.in. coordinator_single) – max 1 zdjęcie */}
            {!task.is_extra_task && photo_required && !hasPhoto && (
              <>
                <input
                  ref={cameraInputRef}
                  type="file"
                  accept="image/*"
                  capture="environment"
                  className="hidden"
                  onChange={(e) => handleFileChange(e)}
                  disabled={disabled || isUploading || isProcessingImage}
                />
                <Button
                  variant="ghost"
                  size="icon"
                  disabled={disabled || isUploading || isProcessingImage}
                  className={cn(
                    "h-10 w-10",
                    !hasEnoughPhotos && "text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300"
                  )}
                  onClick={handleCameraClick}
                  title={!hasEnoughPhotos ? "Zdjęcie jest wymagane" : "Zrób zdjęcie"}
                >
                  <Camera className="h-6 w-6" />
                </Button>
              </>
            )}
            
            {/* Przyciski zdjęć - zadania dodatkowe (Przed/Po) – max 1 na slot */}
            {task.is_extra_task && (
              <>
                {!hasPhotoBefore && (
                  <>
                    <input
                      ref={fileInputRefBefore}
                      type="file"
                      accept="image/*"
                      capture="environment"
                      className="hidden"
                      onChange={(e) => handleFileChange(e, 0)}
                      disabled={disabled || isUploading || isProcessingImage}
                    />
                    <Button
                      variant="ghost"
                      size="icon"
                      disabled={disabled || (isUploading && uploadingIndex === 0) || (isSaving && uploadingIndex === 0)}
                      className="h-10 w-10"
                      onClick={handlePhotoClickBefore}
                      title="Zdjęcie przed"
                    >
                      {(isUploading && uploadingIndex === 0) || (isSaving && uploadingIndex === 0) ? (
                        <Loader2 className="h-6 w-6 animate-spin" />
                      ) : (
                        <Camera className="h-6 w-6" />
                      )}
                    </Button>
                  </>
                )}
                {hasPhotoBefore && (
                  <span className="h-10 w-10 flex items-center justify-center text-green-600 dark:text-green-400" title="Zdjęcie przed dodane">
                    <Check className="h-6 w-6" />
                  </span>
                )}
                {!hasPhotoAfter && (
                  <>
                    <input
                      ref={fileInputRefAfter}
                      type="file"
                      accept="image/*"
                      capture="environment"
                      className="hidden"
                      onChange={(e) => handleFileChange(e, 1)}
                      disabled={disabled || isUploading || isProcessingImage}
                    />
                    <Button
                      variant="ghost"
                      size="icon"
                      disabled={disabled || (isUploading && uploadingIndex === 1) || (isSaving && uploadingIndex === 1)}
                      className="h-10 w-10"
                      onClick={handlePhotoClickAfter}
                      title="Zdjęcie po"
                    >
                      {(isUploading && uploadingIndex === 1) || (isSaving && uploadingIndex === 1) ? (
                        <Loader2 className="h-6 w-6 animate-spin" />
                      ) : (
                        <Camera className="h-6 w-6" />
                      )}
                    </Button>
                  </>
                )}
                {hasPhotoAfter && (
                  <span className="h-10 w-10 flex items-center justify-center text-green-600 dark:text-green-400" title="Zdjęcie po dodane">
                    <Check className="h-6 w-6" />
                  </span>
                )}
              </>
            )}
            
            {/* Przycisk przełożenia zadania (Popover z datą) */}
            {task.task_type === 'sop_standard' && onRescheduleTask && task.status !== 'moved' && !task.completed && (
              <ReschedulePopover
                taskId={task.id}
                disabled={disabled}
                isSaving={isSaving}
                onReschedule={onRescheduleTask}
              />
            )}
            
            {/* Przycisk cofnięcia przeniesienia */}
            {task.status === 'moved' && onUndoMove && (
              <Button
                variant="ghost"
                size="icon"
                disabled={isSaving || isUploading}
                className="h-10 w-10 text-orange-600 hover:text-orange-700 dark:text-orange-400 dark:hover:text-orange-300"
                onClick={() => !isSaving && !isUploading && onUndoMove(task.id)}
                title="Cofnij przeniesienie"
              >
                <RotateCcw className="h-6 w-6" />
              </Button>
            )}
            
            {/* Przycisk cofnięcia ukończenia */}
            {task.completed && onUndoComplete && (
              <Button
                variant="ghost"
                size="icon"
                disabled={isSaving || isUploading || disabled}
                className="h-10 w-10 text-gray-600 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
                onClick={() => !isSaving && !isUploading && !disabled && onUndoComplete(task.id)}
                title="Cofnij ukończenie"
              >
                <RotateCcw className="h-6 w-6" />
              </Button>
            )}
            
            {/* Przycisk usuwania dla zadań dodatkowych — zawsze aktywny, nawet gdy reszta checklisty jest zablokowana */}
            {task.is_extra_task && onDelete && (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-10 w-10 shrink-0 text-destructive hover:text-destructive"
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  onDelete(task.id);
                }}
                title="Usuń zadanie dodatkowe"
              >
                <Trash2 className="h-6 w-6" />
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Wskaźnik przetwarzania zdjęcia (standardowe zadania) */}
      {!task.is_extra_task && photo_required && isProcessingImage && (
        <div className="ml-[76px] p-3 rounded-lg bg-muted/50 flex items-center gap-2 text-muted-foreground text-sm">
          <Loader2 className="h-5 w-5 animate-spin flex-shrink-0" />
          <span>Trwa optymalizacja zdjęcia...</span>
        </div>
      )}
      {/* Podgląd zdjęcia przed wysyłką (tylko dla standardowych zadań) - układ mobilny */}
      {!task.is_extra_task && photo_required && previewUrl && !isProcessingImage && (
        <div className="ml-[76px] p-3 rounded-lg bg-muted/50 space-y-2">
          {/* Wizualizacja przywróconego draftu zdjęcia */}
          {restoredDraft?.photo && (
            <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 dark:bg-blue-950 dark:text-blue-300 border-blue-300 dark:border-blue-700">
              Przywrócono wersję roboczą
            </Badge>
          )}
          <div className="flex flex-row gap-3 items-center">
            {/* Miniaturka po lewej */}
            <div className="rounded-lg overflow-hidden border-2 border-zinc-200 dark:border-zinc-700 flex-shrink-0">
              <img
                src={previewUrl}
                alt="Podgląd zdjęcia"
                className="h-20 w-20 object-cover"
              />
            </div>
            {/* Przyciski po prawej - ikony (oszczędność miejsca) */}
            <div className="flex flex-row gap-2 flex-1 items-center">
              <Button
                size="icon"
                className="h-10 w-10 shrink-0 bg-green-600 hover:bg-green-700 text-white"
                onClick={handlePhotoSend}
                disabled={disabled || isUploading || isProcessingImage}
                title="Zapisz zdjęcie"
              >
                {isUploading ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  <Check className="h-5 w-5" />
                )}
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-10 w-10 shrink-0 text-muted-foreground hover:text-destructive"
                onClick={handleClearPreview}
                disabled={disabled || isUploading || isProcessingImage}
                title="Anuluj"
              >
                <X className="h-5 w-5" />
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Wskaźnik przetwarzania zdjęcia (zadania dodatkowe) */}
      {task.is_extra_task && isProcessingImage && (
        <div className="ml-[76px] p-3 rounded-lg bg-muted/50 flex items-center gap-2 text-muted-foreground text-sm">
          <Loader2 className="h-5 w-5 animate-spin flex-shrink-0" />
          <span>Trwa optymalizacja zdjęcia...</span>
        </div>
      )}
      {/* Podgląd zdjęć "Przed i Po" dla zadań dodatkowych */}
      {task.is_extra_task && (previewUrlBefore || previewUrlAfter) && !isProcessingImage && (
        <div className="ml-[76px] space-y-3">
          {/* Wizualizacja przywróconego draftu zdjęcia */}
          {restoredDraft?.photo && (
            <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 dark:bg-blue-950 dark:text-blue-300 border-blue-300 dark:border-blue-700">
              Przywrócono wersję roboczą
            </Badge>
          )}
          {/* Podgląd zdjęcia "Przed" */}
          {previewUrlBefore && (
            <div className="p-3 rounded-lg bg-muted/50">
              <div className="flex flex-row gap-3 items-center">
                {/* Miniaturka po lewej */}
                <div className="rounded-lg overflow-hidden border-2 border-zinc-200 dark:border-zinc-700 flex-shrink-0">
                  <img
                    src={previewUrlBefore}
                    alt="Podgląd zdjęcia przed"
                    className="h-20 w-20 object-cover"
                  />
                </div>
                {/* Przyciski po prawej - ikony (oszczędność miejsca) */}
                <div className="flex flex-row gap-2 flex-1 items-center">
                  <Button
                    size="icon"
                    className="h-10 w-10 shrink-0 bg-green-600 hover:bg-green-700 text-white"
                    onClick={handlePhotoSendBefore}
                    disabled={disabled || isUploading || isProcessingImage || (uploadingIndex !== null && uploadingIndex !== 0)}
                    title="Zapisz zdjęcie przed"
                  >
                    {(isUploading && uploadingIndex === 0) ? (
                      <Loader2 className="h-5 w-5 animate-spin" />
                    ) : (
                      <Check className="h-5 w-5" />
                    )}
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-10 w-10 shrink-0 text-muted-foreground hover:text-destructive"
                    onClick={handleClearPreviewBefore}
                    disabled={disabled || isUploading || isProcessingImage}
                    title="Anuluj"
                  >
                    <X className="h-5 w-5" />
                  </Button>
                </div>
              </div>
            </div>
          )}
          
          {/* Podgląd zdjęcia "Po" */}
          {previewUrlAfter && (
            <div className="p-3 rounded-lg bg-muted/50">
              <div className="flex flex-row gap-3 items-center">
                {/* Miniaturka po lewej */}
                <div className="rounded-lg overflow-hidden border-2 border-zinc-200 dark:border-zinc-700 flex-shrink-0">
                  <img
                    src={previewUrlAfter}
                    alt="Podgląd zdjęcia po"
                    className="h-20 w-20 object-cover"
                  />
                </div>
                {/* Przyciski po prawej - ikony (oszczędność miejsca) */}
                <div className="flex flex-row gap-2 flex-1 items-center">
                  <Button
                    size="icon"
                    className="h-10 w-10 shrink-0 bg-green-600 hover:bg-green-700 text-white"
                    onClick={handlePhotoSendAfter}
                    disabled={disabled || isUploading || isProcessingImage || (uploadingIndex !== null && uploadingIndex !== 1)}
                    title="Zapisz zdjęcie po"
                  >
                    {(isUploading && uploadingIndex === 1) ? (
                      <Loader2 className="h-5 w-5 animate-spin" />
                    ) : (
                      <Check className="h-5 w-5" />
                    )}
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-10 w-10 shrink-0 text-muted-foreground hover:text-destructive"
                    onClick={handleClearPreviewAfter}
                    disabled={disabled || isUploading || isProcessingImage}
                    title="Anuluj"
                  >
                    <X className="h-5 w-5" />
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Photo gallery - miniaturki zdjęć (tylko faktycznie wysłane zdjęcia z photo_urls) */}
      {photo_required && photo_urls.length > 0 && (
        <div className="ml-[76px] space-y-2">
          <div className="flex flex-wrap gap-2">
            {photo_urls.map((url, index) => {
              const isSynced = typeof url === 'string' && !isLocalPhotoRef(url);
              return (
                <div
                  key={`${index}-${isSynced ? 'synced' : 'offline'}`}
                  className={cn(
                    "relative group rounded-lg overflow-hidden border-2",
                    isSynced
                      ? "border-green-500 dark:border-green-600"
                      : "border-amber-500 dark:border-amber-600"
                  )}
                >
                  <StoragePhoto
                    pathOrUrl={url}
                    fallbackBucket="photos"
                    alt={`Zdjęcie ${index + 1} dla ${task.name}`}
                    className="h-20 w-20 object-cover"
                  />
                  {!isSynced && (
                    <span
                      className="absolute top-1 right-1 bg-amber-500/90 text-white text-[10px] px-1.5 py-0.5 rounded flex items-center gap-0.5"
                      title="Zdjęcie offline – zostanie zsynchronizowane"
                    >
                      <CloudOff className="h-3 w-3" />
                      Offline
                    </span>
                  )}
                  {!disabled && onPhotoDelete && (
                    <button
                      onClick={() => handlePhotoDelete(url)}
                      className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                      title="Usuń zdjęcie"
                    >
                      <Trash2 className="h-5 w-5 text-white" />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
          {!hasEnoughPhotos && (
            <p className="text-xs text-amber-600 dark:text-amber-400">
              Wymagane minimum {min_photos} {min_photos === 1 ? 'zdjęcie' : 'zdjęć'}. 
              Dodaj jeszcze {min_photos - photo_urls.length} {min_photos - photo_urls.length === 1 ? 'zdjęcie' : 'zdjęć'}.
            </p>
          )}
        </div>
      )}

      {/* Note input */}
      {showNoteInput && (
        <div className="ml-4 p-3 rounded-lg bg-muted/50 space-y-2">
          {/* Wizualizacja przywróconego draftu notatki */}
          {restoredDraft?.note && (
            <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 dark:bg-blue-950 dark:text-blue-300 border-blue-300 dark:border-blue-700">
              Przywrócono wersję roboczą
            </Badge>
          )}
          <div className="relative">
            <Textarea
              value={localNote}
              onChange={(e) => handleNoteChange(e.target.value)}
              placeholder="Dodaj uwagę do tej czynności..."
              className="min-h-[60px] resize-none bg-white dark:bg-zinc-900 pr-10"
              disabled={disabled}
            />
            {/* Microphone button */}
            {typeof window !== 'undefined' && (window.SpeechRecognition || window.webkitSpeechRecognition) && (
              <Button
                variant="ghost"
                size="icon"
                disabled={disabled}
                className={cn(
                  "absolute right-2 top-2 h-7 w-7",
                  isListening && "bg-red-100 text-red-600 dark:bg-red-950 dark:text-red-400 animate-pulse"
                )}
                onClick={handleVoiceInput}
                title={isListening ? 'Zatrzymaj dyktowanie' : 'Dyktuj notatkę'}
              >
                <Mic className="h-4 w-4" />
              </Button>
            )}
          </div>
          <div className="flex justify-end gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleCancel}
            >
              <X className="h-4 w-4 mr-1" />
              Anuluj
            </Button>
            <Button
              size="sm"
              onClick={handleNoteSubmit}
            >
              Zapisz
            </Button>
          </div>
        </div>
      )}

      {/* Saved note display */}
      {task.note && task.note.trim() !== '' && !showNoteInput && (
        <div className="ml-[76px] text-sm text-muted-foreground italic">
          {task.note}
        </div>
      )}
    </div>
  );
};
